/* eslint-disable no-console */
import React, { createContext, useContext, useEffect, ReactNode } from 'react';

import { getMinerOutputIntervalSeconds } from '../BeltItemSystem';
import {
  GAME_CONFIG,
  BUILDINGS,
  BuildingType,
  BuildMenuState,
  Recipe,
  ItemType,
  ResearchId,
  BeltItem,
  BuildingItemRequirement,
  RESEARCHES,
} from '../constants';
import { getGameEngine } from '../GameEngine';
import {
  GameMap,
  setAssemblerRecipe,
  getAssemblerInventory,
  addItemToAssembler,
  AssemblerInventoryLayer,
  ChestInventoryLayer,
  getAvailableItemCountsForPlacement,
  getPlayerInventoryItemCounts,
  getBuildingAtPosition,
  findBuildingByCharacter,
} from '../GameState';
import {
  getCurrentTick,
  waitForTicks,
  waitForNextTick,
  resetTickCount,
  enableTestingMode,
  disableTestingMode,
  advanceGameTick,
  isInTestingMode,
  getTickSubscriptionCount,
  isTickSystemRunning,
  enableDebugStepMode,
  disableDebugStepMode,
  stepAndPause,
  isInDebugStepMode,
} from '../hooks/useGameTick';
import { UseWorldReturn } from '../hooks/useWorld';
import {
  serializeResearchState,
  getBeltCapacityMultiplier,
  getMinerSpeedMultiplier,
  getRepeatableResearchLevel,
  isResearchUnlocked,
} from '../ResearchSystem';
import { TestRunner } from '../TestRunner';
import { runAllGameTests } from '../tests';
import { TestGlobals } from '../types/TestGlobals';
import { secondsToTicks } from '../utils/TickIntervals';

// Position type (simple x,y coordinates)
interface Position {
  x: number;
  y: number;
}

// Test global manager context interface
interface TestGlobalManagerContextProps {
  children: ReactNode;
  worldState?: UseWorldReturn | null;
  buildingState?: {
    buildMode: boolean; // Derived from selectedBuilding !== null
    selectedCategory: string | null;
    selectedBuilding: { id: string; name: string } | null;
    buildMenuOpen: BuildMenuState;
  } | null;
}

// Context for managing test globals (not used directly, just for component structure)
const TestGlobalManagerContext = createContext<Record<string, never>>({});

// Hook to use test global manager context
export const useTestGlobalManager = () => {
  return useContext(TestGlobalManagerContext);
};

// Test global manager component
export const TestGlobalManager: React.FC<TestGlobalManagerContextProps> = ({
  children,
  worldState,
  buildingState,
}) => {
  useEffect(() => {
    // Global function to run tests from browser console
    const runGameTests = async (): Promise<void> => {
      try {
        console.clear();
        await runAllGameTests();
      } catch (error) {
        console.error('❌ Test execution failed:', error);
      }
    };

    // Create empty 100x100 world with grass
    const createEmptyWorld = (): void => {
      try {
        const engine = getGameEngine();

        // Clear existing world and create 100x100 grass terrain
        const newGameMap: GameMap = {};
        for (let y = 0; y < 100; y++) {
          for (let x = 0; x < 100; x++) {
            const key = `${x},${y}`;
            // Create borders
            if (x === 0 || x === 99 || y === 0 || y === 99) {
              newGameMap[key] = GAME_CONFIG.WALL_CHARACTER;
            } else {
              newGameMap[key] = GAME_CONFIG.EMPTY_CHARACTER;
            }
          }
        }

        // Update engine state with the new world
        engine.setState({
          gameMap: newGameMap,
          buildingLayer: {},
          beltContentLayer: {},
          assemblerInventoryLayer: {},
          chestInventoryLayer: {},
          rejectedItemsLayer: {},
          minerTimerLayer: {},
          minerOutputBufferLayer: {},
          playerPos: { x: 50, y: 50 },
          cameraPos: { x: 50, y: 50 },
        });
      } catch (error) {
        console.error('❌ Failed to create empty world:', error);
      }
    };

    // Place a resource node at specific coordinates
    const placeResourceNode = (
      x: number,
      y: number,
      nodeType: 'copper' | 'iron'
    ): void => {
      try {
        const character =
          nodeType === 'copper'
            ? GAME_CONFIG.COPPER_NODE_CHARACTER
            : GAME_CONFIG.IRON_NODE_CHARACTER;

        const engine = getGameEngine();
        const currentState = engine.getState();

        // Place the resource node in the game map
        const key = `${x},${y}`;
        const newGameMap = { ...currentState.gameMap };
        newGameMap[key] = character;

        engine.setState({ gameMap: newGameMap });
      } catch (error) {
        console.error(`❌ Failed to place ${nodeType} node:`, error);
      }
    };

    // Get current player position
    const getPlayerPosition = (): { x: number; y: number } | null => {
      try {
        const engine = getGameEngine();
        return engine.getState().playerPos;
      } catch (error) {
        console.error('❌ Failed to get player position:', error);
        return null;
      }
    };

    // Set player facing direction
    const setPlayerFacing = (
      direction: 'up' | 'down' | 'left' | 'right'
    ): void => {
      try {
        const engine = getGameEngine();
        engine.setState({ playerFacing: direction });
      } catch (error) {
        console.error('❌ Failed to set player facing:', error);
      }
    };

    // Place a test building
    const placeTestBuilding = (
      x: number,
      y: number,
      buildingId: BuildingType
    ): boolean => {
      try {
        const engine = getGameEngine();
        const engineState = engine.getState();

        const availableItems = getAvailableItemCountsForPlacement(
          engineState.playerInventory,
          engineState.chestInventoryLayer
        );

        // Find the building by ID
        const building = Object.values(BUILDINGS).find(
          b => b.id === buildingId
        );
        if (!building) {
          console.error(`❌ Building '${buildingId}' not found`);
          console.error(
            'Available building IDs:',
            Object.values(BUILDINGS).map(b => b.id)
          );
          return false;
        }

        const itemRequirements: readonly BuildingItemRequirement[] =
          building.cost.items ?? [];

        const success = engine.placeBuilding(x, y, building);

        // Debug logging for failed placements
        if (!success) {
          const canAffordBuilding = itemRequirements.every(
            ({ item, amount }) => (availableItems[item] ?? 0) >= amount
          );

          console.error(`❌ Building placement failed for ${buildingId}`, {
            position: `${x},${y}`,
            buildingName: building.name,
            buildingSize: building.size,
            buildingCost: building.cost,
            canAffordBuilding,
            itemAvailability:
              itemRequirements.length > 0
                ? itemRequirements.map(({ item, amount }) => ({
                    item,
                    required: amount,
                    available: availableItems[item] ?? 0,
                  }))
                : undefined,
            playerPosition: engineState.playerPos,
            tileAtPosition: engineState.gameMap[`${x},${y}`],
            existingBuilding: engineState.buildingLayer[`${x},${y}`],
          });

          // Check all positions the building would occupy for multi-tile buildings
          if (building.size) {
            console.error('Multi-tile building position check:');
            const size = building.size;
            const offsetX = Math.floor(size.width / 2);
            const offsetY = Math.floor(size.height / 2);

            for (let dy = 0; dy < size.height; dy++) {
              for (let dx = 0; dx < size.width; dx++) {
                const posX = x - offsetX + dx;
                const posY = y - offsetY + dy;
                const posKey = `${posX},${posY}`;
                const terrain = engineState.gameMap[posKey];
                const existingBuilding = engineState.buildingLayer[posKey];
                const isPlayerPosition =
                  engineState.playerPos.x === posX &&
                  engineState.playerPos.y === posY;

                console.error(
                  `  Position (${posX},${posY}): terrain="${terrain}", building="${existingBuilding || 'none'}", isPlayer=${isPlayerPosition}`
                );
              }
            }
          }
        }

        return success;
      } catch (error) {
        console.error(`❌ Failed to place building ${buildingId}:`, error);
        return false;
      }
    };

    const getBuildingInfoAtPosition = (
      x: number,
      y: number
    ): {
      buildingId: BuildingType | null;
      buildingName: string | null;
      center: Position | null;
    } => {
      try {
        const engine = getGameEngine();
        const { building, centerPosition } = getBuildingAtPosition(
          x,
          y,
          engine.getState().buildingLayer
        );

        if (!building) {
          return { buildingId: null, buildingName: null, center: null };
        }

        return {
          buildingId: building.id,
          buildingName: building.name,
          center: centerPosition,
        };
      } catch (error) {
        console.error('❌ Failed to get building at position:', {
          x,
          y,
          error,
        });
        return { buildingId: null, buildingName: null, center: null };
      }
    };

    // Get current game state for debugging
    const getGameState = (): unknown => {
      try {
        const engine = getGameEngine();
        const state = engine.getState();

        return {
          playerPos: state.playerPos,
          playerFacing: state.playerFacing,
          playerInventory: state.playerInventory,
          playerInventoryCounts: getPlayerInventoryItemCounts(
            state.playerInventory
          ),
          cameraPos: state.cameraPos,
          currentBiome: state.currentBiome,
          zoomLevel: state.zoomLevel,
          isLoading: worldState?.isLoading || false,
          gameMapSize: Object.keys(state.gameMap).length,
          buildingCount: Object.keys(state.buildingLayer).length,
          beltItemCount: Object.values(state.beltContentLayer).reduce(
            (total, items) => total + items.length,
            0
          ),
          // Include actual layers for debugging
          researchState: state.researchState,
          beltContentLayer: state.beltContentLayer,
          buildingLayer: state.buildingLayer,
          gameMap: state.gameMap,
          minerTimerLayer: state.minerTimerLayer,
          assemblerInventoryLayer: state.assemblerInventoryLayer,
          chestInventoryLayer: state.chestInventoryLayer,
          rejectedItemsLayer: state.rejectedItemsLayer,
          // Include UI state for building preview tests
          buildMode: buildingState?.buildMode || false, // Now from GameEngine state
          selectedBuilding: buildingState?.selectedBuilding || null,
          buildMenuOpen: buildingState?.buildMenuOpen || false,
          selectedCategory: buildingState?.selectedCategory || null,
        };
      } catch (error) {
        console.error('❌ Failed to get game state:', error);
        return null;
      }
    };

    // Create a test world with player facing an iron node
    const createTestWorld = (): void => {
      try {
        createEmptyWorld();
        setPlayerFacing('right');
        placeResourceNode(51, 50, 'iron'); // Place iron node to the right of player
      } catch (error) {
        console.error('❌ Failed to create test world:', error);
      }
    };

    const rebuildCollisionSystem = (): void => {
      try {
        const engine = getGameEngine();
        const currentLayer = engine.getState().buildingLayer;
        engine.setState({ buildingLayer: { ...currentLayer } });
      } catch (error) {
        console.error('❌ Failed to rebuild collision system:', error);
      }
    };

    // Get ticks until miner output (for testing)
    const getMinerTicksUntilOutput = (x: number, y: number): number => {
      try {
        const engine = getGameEngine();
        const state = engine.getState();
        const currentTick = getCurrentTick();

        const posKey = `${x},${y}`;
        const buildingChar = state.buildingLayer[posKey];
        const building =
          buildingChar !== undefined
            ? findBuildingByCharacter(buildingChar)
            : null;

        if (!building || building.category !== 'mining') {
          return 0;
        }

        const minerSpeedMultiplier = getMinerSpeedMultiplier(
          state.researchState
        );
        const minerIntervalSeconds = getMinerOutputIntervalSeconds(
          minerSpeedMultiplier,
          building
        );
        const minerOutputIntervalTicks = Math.max(
          1,
          secondsToTicks(minerIntervalSeconds)
        );
        const minerStartTick = state.minerTimerLayer[posKey];

        if (minerStartTick === undefined) {
          return minerOutputIntervalTicks;
        }

        if (minerStartTick > currentTick) {
          return minerOutputIntervalTicks;
        }

        const nextOutputTick = minerStartTick + minerOutputIntervalTicks;
        const ticksUntilOutput = nextOutputTick - currentTick;

        return Math.max(0, ticksUntilOutput);
      } catch (error) {
        console.error('❌ Failed to get miner output ticks:', error);
        return 0;
      }
    };

    // Force mining system to run (test-only bypass) - now uses GameEngine
    const forceMiningUpdate = (): void => {
      try {
        const engine = getGameEngine();
        // Force a game tick - this will process mining, belt movement, and crafting
        engine.processTick();
      } catch (error) {
        console.error('❌ Failed to force mining update:', error);
      }
    };

    // Add starter resource-equivalent items
    const addResources = (amount: number): void => {
      try {
        const engine = getGameEngine();
        const coreItems: ItemType[] = ['iron-plate', 'gear', 'circuit'];
        coreItems.forEach(itemType => {
          engine.addItemToInventory(itemType, amount);
        });
      } catch (error) {
        console.error('❌ Failed to add starter items:', error);
      }
    };

    const resourceSetup = (
      amount: number = 500
    ): {
      hasAddResources: boolean;
      inventoryCounts: Partial<Record<ItemType, number>> | null;
    } => {
      const hasAddResources = typeof addResources === 'function';
      addResources?.(amount);
      const gameState = getGameState() as {
        playerInventoryCounts?: Partial<Record<ItemType, number>>;
      } | null;

      return {
        hasAddResources,
        inventoryCounts: gameState?.playerInventoryCounts ?? null,
      };
    };

    const addItems = (itemType: ItemType, amount: number): number => {
      try {
        const engine = getGameEngine();
        return engine.addItemToInventory(itemType, amount);
      } catch (error) {
        console.error('❌ Failed to add items:', error);
        return 0;
      }
    };

    // Recipe management functions (generic, works for assemblers and other recipe-using buildings)
    const testSetRecipe = (
      assemblerInventoryLayer: AssemblerInventoryLayer,
      position: Position,
      recipe: Recipe | null
    ): AssemblerInventoryLayer => {
      const engine = getGameEngine();
      const state = engine.getState();
      return setAssemblerRecipe(
        assemblerInventoryLayer,
        state.buildingLayer,
        position,
        recipe,
        state.researchState
      );
    };

    const testGetAssemblerInventory = (
      assemblerInventoryLayer: AssemblerInventoryLayer,
      position: Position
    ) => {
      const engine = getGameEngine();
      const state = engine.getState();
      return getAssemblerInventory(
        assemblerInventoryLayer,
        state.buildingLayer,
        position
      );
    };

    const testAddItemToAssembler = (
      assemblerInventoryLayer: AssemblerInventoryLayer,
      position: Position,
      itemType: ItemType,
      count: number = 1
    ): AssemblerInventoryLayer => {
      const engine = getGameEngine();
      const state = engine.getState();
      return addItemToAssembler(
        assemblerInventoryLayer,
        state.buildingLayer,
        position,
        itemType,
        count,
        state.researchState
      );
    };

    // Set assembler inventory layer
    const setAssemblerInventoryLayer = (
      newLayer: AssemblerInventoryLayer
    ): void => {
      try {
        const engine = getGameEngine();
        engine.setState({ assemblerInventoryLayer: newLayer });
      } catch (error) {
        console.error('❌ Failed to set assembler inventory layer:', error);
      }
    };

    const setChestInventoryLayer = (newLayer: ChestInventoryLayer): void => {
      try {
        const engine = getGameEngine();
        engine.setState({ chestInventoryLayer: newLayer });
      } catch (error) {
        console.error('❌ Failed to set chest inventory layer:', error);
      }
    };

    const setBeltContentLayer = (
      newLayer: Record<string, BeltItem[]>
    ): void => {
      try {
        const engine = getGameEngine();
        engine.setState({ beltContentLayer: newLayer });
      } catch (error) {
        console.error('❌ Failed to set belt content layer:', error);
      }
    };

    // Move player to specific position
    const movePlayerTo = (x: number, y: number): void => {
      try {
        const engine = getGameEngine();
        engine.movePlayer(x, y);
      } catch (error) {
        console.error('❌ Failed to move player:', error);
      }
    };

    const setHoverTargetPositionForTesting = (
      position: Position | null
    ): void => {
      try {
        const engine = getGameEngine();
        engine.setHoverTargetPosition(position);
      } catch (error) {
        console.error('❌ Failed to set hover target position:', error);
      }
    };

    // Advance game ticks through the engine (for testing)
    const advanceEngineGameTick = (count: number = 1): void => {
      try {
        const engine = getGameEngine();
        for (let i = 0; i < count; i++) {
          // Advance the tick counter
          advanceGameTick(1);
          // Process the game logic
          engine.processTick();
        }
      } catch (error) {
        console.error('❌ Failed to advance engine game tick:', error);
      }
    };

    const setUnlimitedPower = (enabled: boolean): void => {
      try {
        const engine = getGameEngine();
        engine.setUnlimitedPowerMode(enabled);
      } catch (error) {
        console.error('❌ Failed to set unlimited power mode:', error);
      }
    };

    const isUnlimitedPowerEnabled = (): boolean => {
      try {
        const engine = getGameEngine();
        return engine.isUnlimitedPowerModeEnabled();
      } catch (error) {
        console.error('❌ Failed to read unlimited power state:', error);
        return false;
      }
    };

    // Create the test globals object
    const testGlobals: TestGlobals = {
      runGameTests,
      TestRunner,
      runAllGameTests,
      // Tick system methods for deterministic testing
      getCurrentTick,
      waitForTicks,
      waitForNextTick,
      resetTickCount,
      // Testing mode methods
      enableTestingMode,
      disableTestingMode,
      advanceGameTick,
      isInTestingMode,
      setUnlimitedPower,
      isUnlimitedPowerEnabled,
      getTickSubscriptionCount,
      isTickSystemRunning,
      // World manipulation methods
      createEmptyWorld,
      placeResourceNode,
      getPlayerPosition,
      setPlayerFacing,
      placeTestBuilding,
      getMinerTicksUntilOutput,
      forceMiningUpdate,
      getGameState,
      createTestWorld,
      rebuildCollisionSystem,
      addResources,
      resourceSetup,
      getBuildingAtPosition: getBuildingInfoAtPosition,
      addItems,
      launchRocket: () => {
        try {
          const engine = getGameEngine();
          engine.launchRocket();
        } catch (error) {
          console.error('❌ Failed to launch rocket:', error);
        }
      },
      getPrestigeState: () => {
        try {
          const engine = getGameEngine();
          const { prestigePoints, prestigeCount } = engine.getState();
          return { prestigePoints, prestigeCount };
        } catch (error) {
          console.error('❌ Failed to read prestige state:', error);
          return { prestigePoints: 0, prestigeCount: 0 };
        }
      },
      // Recipe and inventory functions
      setRecipe: testSetRecipe,
      getAssemblerInventory: testGetAssemblerInventory,
      addItemToAssembler: testAddItemToAssembler,
      setAssemblerInventoryLayer,
      setChestInventoryLayer,
      setBeltContentLayer,
      movePlayerTo,
      setHoverTargetPosition: setHoverTargetPositionForTesting,
      // Engine-specific functions
      advanceEngineGameTick,
      unlockResearch: (researchId: ResearchId) => {
        try {
          const engine = getGameEngine();
          engine.unlockResearch(researchId);
        } catch (error) {
          console.error('❌ Failed to unlock research:', error);
        }
      },
      isResearchUnlocked: (researchId: ResearchId): boolean => {
        try {
          const engine = getGameEngine();
          return engine.isResearchUnlocked(researchId);
        } catch (error) {
          console.error('❌ Failed to check research state:', error);
          return false;
        }
      },
      getUnlockedResearchIds: (): ResearchId[] => {
        try {
          const engine = getGameEngine();
          return serializeResearchState(engine.getResearchState());
        } catch (error) {
          console.error('❌ Failed to list unlocked research:', error);
          return [];
        }
      },
      selectResearch: (researchId: ResearchId | null) => {
        try {
          const engine = getGameEngine();
          engine.selectResearch(researchId);
        } catch (error) {
          console.error('❌ Failed to set active research:', error);
        }
      },
      addResearchItems: (
        researchId: ResearchId,
        itemType: ItemType,
        amount: number
      ) => {
        try {
          const engine = getGameEngine();
          return engine.addResearchItems(researchId, itemType, amount);
        } catch (error) {
          console.error('❌ Failed to add research items:', error);
          return { accepted: false, completed: false };
        }
      },
      testForceUnlockResearch: (
        researchId: ResearchId,
        targetLevel: number = 1
      ) => {
        try {
          const engine = getGameEngine();
          const desiredLevel = Math.max(0, targetLevel);
          const { hasEnabledDebug, researchState } = engine.getState();
          const shouldRestoreDebug = !hasEnabledDebug;

          if (shouldRestoreDebug) {
            engine.setHasEnabledDebug(true);
          }

          const isRepeatableResearch = Boolean(
            RESEARCHES[researchId]?.repeatable
          );

          if (isRepeatableResearch) {
            const currentLevel = getRepeatableResearchLevel(
              researchState,
              researchId
            );
            for (
              let levelIndex = currentLevel;
              levelIndex < desiredLevel;
              levelIndex += 1
            ) {
              engine.forceUnlockResearch(researchId);
            }
          } else if (
            desiredLevel > 0 &&
            !isResearchUnlocked(researchState, researchId)
          ) {
            engine.forceUnlockResearch(researchId);
          }

          if (shouldRestoreDebug) {
            engine.setHasEnabledDebug(false);
          }
        } catch (error) {
          console.error('❌ Failed to force unlock research:', error);
        }
      },
      setResearchMenuOpen: (open: boolean) => {
        try {
          const engine = getGameEngine();
          engine.setResearchMenuOpen(open);
        } catch (error) {
          console.error('❌ Failed to set research menu state:', error);
        }
      },
      getBeltCapacityMultiplier,
      // Debug step mode methods
      enableDebugStepMode,
      disableDebugStepMode,
      stepAndPause,
      isInDebugStepMode,
    };

    // Expose test globals under window.__TestGlobal
    if (typeof window !== 'undefined') {
      (window as Window & { __TestGlobal?: TestGlobals }).__TestGlobal =
        testGlobals;

      // Also maintain backward compatibility with existing testGlobals
      (window as Window & { testGlobals?: TestGlobals }).testGlobals =
        testGlobals;

      // Expose BUILDINGS constant for debugging
      (window as Window & { BUILDINGS?: typeof BUILDINGS }).BUILDINGS =
        BUILDINGS;

      // Test globals initialized (removed console spam)
    }

    // Cleanup function
    return () => {
      if (typeof window !== 'undefined') {
        delete (
          window as Window & {
            __TestGlobal?: TestGlobals;
            testGlobals?: TestGlobals;
          }
        ).__TestGlobal;
        delete (
          window as Window & {
            __TestGlobal?: TestGlobals;
            testGlobals?: TestGlobals;
          }
        ).testGlobals;
      }
    };
  }, [worldState, buildingState]);

  // Provide empty context value (this component is mainly for side effects)
  return (
    <TestGlobalManagerContext.Provider value={{}}>
      {children}
    </TestGlobalManagerContext.Provider>
  );
};
